import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Component, HostListener, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
// Store & Actions
import { Store } from "@ngrx/store";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { getCurrentSelectedReportsCount } from "@partikelart/comparison/feature";
import {
	changeCurrentSite,
	getCurrentSiteOption,
	getUserInformation,
	loggedIn,
	loginUser,
	SiteOption,
	UserInformation,
} from "@partikelart/identity/data-access-user";
import { AuthService } from "@partikelart/portal/data-access-auth";
import { Language } from "@partikelart/portal/shared-typing";
import { NavbarTopButtonComponent } from "@partikelart/portal/ui";
// Services & Utils
import { SearchService, UiInteractionService } from "@partikelart/portal/utils";
import type { SurfaceReport } from "@partikelart/surface/domain";
import * as Sentry from "@sentry/angular";
import { MessageService } from "primeng/api";
import { SelectModule } from "primeng/select";
import { Observable } from "rxjs";
import { debounceTime, distinctUntilChanged, map, take } from "rxjs/operators";
// Configuration
import { ENV_CONFIG } from "../../app.token"; // Adjust path as needed

@Component({
	selector: "app-content-layout",
	templateUrl: "./content-layout.component.html",
	styleUrls: ["./content-layout.component.scss"],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		RouterModule,
		FormsModule,
		TranslatePipe,
		SelectModule,
		NavbarTopButtonComponent,
	],
})
export class ContentLayoutComponent implements OnInit {
	// --- 1. Dependencies ---
	private readonly env = inject(ENV_CONFIG);
	private readonly store = inject(Store);
	private readonly router = inject(Router);
	private readonly searchService = inject(SearchService);
	private readonly authService = inject(AuthService);
	public readonly translate = inject(TranslateService);
	private readonly messageService = inject(MessageService);
	private readonly uiInteractionService = inject(UiInteractionService);
	private readonly http = inject(HttpClient);

	// --- 2. Environment Flags ---
	isProduction = this.env.production;
	demoMode = this.env.demo;
	trapsEnabled = this.env.trapsEnabled !== false;

	// --- 3. UI State & Observables ---
	languages: Language[] = [
		{ name: "EN", code: "en" },
		{ name: "DE", code: "de" },
		{ name: "CS", code: "cs" },
		{ name: "FR", code: "fr" },
		{ name: "KO", code: "ko" },
		{ name: "RO", code: "ro" },
	];
	selectedLanguage!: Language;
	searchTerm = new FormControl("");
	isOpen = false;
	isHamburgerOpen = false;
	isNotificationsOpen = false;
	selectedSiteOption: SiteOption | null = null;

	userInformation$: Observable<UserInformation> = this.store.select(getUserInformation);
	loggedIn$ = this.store.select(loggedIn);
	reportVergleichCount$ = this.store.select(getCurrentSelectedReportsCount);
	currentSiteSelectionServiceObservable$ = this.store.select(getCurrentSiteOption);

	// --- Notification Panel ---
	recentReports$: Observable<SurfaceReport[]> = this.http
		.get<{ data: SurfaceReport[]; total: number }>(
			`${this.env.portalBackendUrl ?? "http://localhost:3000"}/api/surface-reports/summary?limit=5&offset=0`,
		)
		.pipe(map((res) => res.data));

	notificationBadgeCount$: Observable<number> = this.recentReports$.pipe(
		map((reports) => {
			const sevenDaysAgo = new Date();
			sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
			return reports.filter((r) => {
				const analysisDate = r.dateOfAnalysis ? new Date(r.dateOfAnalysis) : null;
				return analysisDate ? analysisDate >= sevenDaysAgo : false;
			}).length;
		}),
	);

	// --- 4. Subscriptions (Field Initializers provide the required Injection Context) ---
	private searchSub = this.searchTerm.valueChanges
		.pipe(debounceTime(100), distinctUntilChanged(), takeUntilDestroyed())
		.subscribe((data) => {
			this.searchService.changeBauteilSearchTerm(data);
		});

	private siteSub = this.store
		.select(getCurrentSiteOption)
		.pipe(takeUntilDestroyed())
		.subscribe((value) => (this.selectedSiteOption = value));

	// --- 5. Lifecycle ---
	ngOnInit() {
		this.store.dispatch(loginUser());

		this.selectedLanguage =
			this.languages.find((lang) => lang.code === this.translate.currentLang()) || this.languages[0];

		// Ensure routes refresh on navigation
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
	}

	// --- 6. Actions ---
	logout() {
		this.authService.logout();
	}

	toggleNotifications(event: Event) {
		event.stopPropagation();
		this.isNotificationsOpen = !this.isNotificationsOpen;
	}

	closeNotifications() {
		this.isNotificationsOpen = false;
	}

	navigateToReport(report: SurfaceReport) {
		this.isNotificationsOpen = false;
		this.router.navigate(["/home/report", report.id]);
	}

	@HostListener("document:click")
	onDocumentClick() {
		this.isNotificationsOpen = false;
	}

	editProfile() {
		Sentry.captureMessage("Edit profile not implemented yet", "warning");
	}

	languageChanged(event: any) {
		this.translate.addLangs(["de"]);
		this.translate.use(this.selectedLanguage.code);

		if (!this.router.url.includes("parts")) {
			setTimeout(() => {
				this.router.navigateByUrl(this.router.url);
			}, 600);
		}
	}

	updateSiteSelection(value: SiteOption) {
		this.store.dispatch(changeCurrentSite({ newSiteId: value.siteId }));
	}

	checkForOptionalNavigation() {
		const urlSplit = this.router.url.split("/");
		const firstElement = urlSplit[1];
		const secondElement = urlSplit[2];

		if (firstElement === "home") {
			const isReport = secondElement === "report" || secondElement === "report-fallback";
			if (isReport || this.router.url.includes("repeat")) {
				this.router.navigate(["/home/dashboard"]);
			}
			if (secondElement === "parts") {
				this.router.navigate(["/home/parts"]);
			}
		}
	}
}
